import React, { useMemo } from 'react';
import {
  Box,
  Flex,
  Group,
  Stack,
  Text,
  ActionIcon,
  Table,
  Paper,
  ScrollArea,
  SegmentedControl,
  UnstyledButton,
  Anchor,
  Popover,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { IconChevronUp, IconChevronDown, IconChevronRight, IconRoute, IconHelp, IconWorld, IconFilter, IconInfoCircle, IconEye, IconEyeOff } from '@tabler/icons-react';
import { GoalRow, Grade, GRADE_LABEL, gradeColors, LifecycleRow, Tone, toneColor } from './types';
import { SortableTh, sortRows, useTableSort } from './sorting';

// Parses display strings like "$18,400" or "1.2d" → number.
/* Tolerates a row that does not carry the field at all - breakdown segment rows legitimately omit
   some - so a missing value reads as 0 instead of throwing on .replace(). Behaviour for a string
   is unchanged. */
const parseNum = (s?: string | number | null) =>
  Number(String(s ?? '').replace(/[^0-9.]/g, '')) || 0;

export const metricDisplayLabel = (metric: string): string => {
  if (metric === 'Spend' || metric === 'Ad Spend') { return 'Total Spend'; }
  /* Display-only renames: the stored keys stay as they are, so saved reports
     keep resolving and only the wording on screen changes.
     'Score and Rating' matches the name of the setting on the webhook screen,
     and covers both shapes the column can hold - an average for a numeric goal,
     a label split for a words goal. 'Values Recorded' avoids tying the metric to
     lead scoring; it serves ratings, satisfaction scores and quantities too. */
  if (metric === 'Avg. Value') { return 'Score and Rating'; }
  if (metric === 'Scored') { return 'Values Recorded'; }
  // Same reason: the column is stored as 'Assisted' in saved reports and in the sort
  // tokens, so only the wording changes here.
  if (metric === 'Assisted') { return 'Assisted Conversion'; }
  return metric;
};

export type MetricSource = {
  spend: string;
  spendTone?: Tone | null;
  revenue: string;
  revenueTone?: Tone | null;
  conversions: number;
  conversionsTone?: Tone | null;
  cpa?: string;
  cpaTone?: Tone | null;
  roas?: string;
  roasTone?: Tone | null;
  uniques?: number;
  uniquesTone?: Tone | null;
  visits?: number;
  visitsTone?: Tone | null;
  goal_visits?: number;
  ucr?: string;
  ucrTone?: Tone | null;
  cr?: string;
  crTone?: Tone | null;
  aov?: string;
  aovTone?: Tone | null;
  epc?: string;
  epcTone?: Tone | null;
  acv?: string;
  acvTone?: Tone | null;
  arpu?: string;
  arpuTone?: Tone | null;
  profit?: string;
  profitTone?: Tone | null;
  roi?: string;
  roiTone?: Tone | null;
};

export type MetricCell = {
  value: string;
  color?: string;
};

// Backend-computed values are preferred; local derivation is a legacy fallback.
export const getMetricCell = (row: MetricSource, metric: string): MetricCell => {
  const spend = parseNum(row.spend);
  const revenue = parseNum(row.revenue);
  const conv = row.conversions;
  const fallbackVisits = Math.max(1, Math.round(conv * 8.2));
  const tint = (tone?: Tone | null): string | undefined =>
    tone ? toneColor(tone) : undefined;

  switch (metric) {
    case 'Spend':
      return { value: row.spend, color: tint(row.spendTone) };
    case 'Revenue':
      return { value: row.revenue, color: tint(row.revenueTone) };
    case 'Conversions':
      return { value: conv.toLocaleString(), color: tint(row.conversionsTone) };
    case 'CPA':
      return {
        value: row.cpa ?? (conv && spend > 0 ? `$${(spend / conv).toFixed(2)}` : '-'),
        color: tint(row.cpaTone),
      };
    case 'ROAS':
      return {
        value: row.roas ?? (spend ? `${(revenue / spend).toFixed(2)}x` : '-'),
        color: tint(row.roasTone),
      };
    case 'Uniques':
      return {
        value:
          typeof row.uniques === 'number'
            ? row.uniques.toLocaleString()
            : Math.round(conv * 6.5).toLocaleString(),
        color: tint(row.uniquesTone),
      };
    case 'Visits':
      return {
        value:
          typeof row.visits === 'number'
            ? row.visits.toLocaleString()
            : fallbackVisits.toLocaleString(),
        color: tint(row.visitsTone),
      };
    case 'UCR':
      return {
        value:
          row.ucr ??
          (typeof row.uniques === 'number' && row.uniques > 0
            ? `${((conv / row.uniques) * 100).toFixed(2)}%`
            : '-'),
        color: tint(row.ucrTone),
      };
    case 'CR':
      return {
        value:
          row.cr ??
          (typeof row.visits === 'number' && row.visits > 0
            ? `${((conv / row.visits) * 100).toFixed(2)}%`
            : '-'),
        color: tint(row.crTone),
      };
    case 'AOV':
      return {
        value: row.aov ?? (conv ? `$${(revenue / conv).toFixed(2)}` : '-'),
        color: tint(row.aovTone),
      };
    // ACV divides by distinct converting sessions, which only the backend knows — no fallback.
    case 'ACV':
      return { value: row.acv ?? '-', color: tint(row.acvTone) };
    // Revenue over unique visitors.
    case 'Unique EPC':
      return { value: row.arpu ?? '-', color: tint(row.arpuTone) };
    case 'EPC': {
      if (row.epc) return { value: row.epc, color: tint(row.epcTone) };
      const v = typeof row.visits === 'number' ? row.visits : fallbackVisits;
      return {
        value: v > 0 ? `$${(revenue / v).toFixed(2)}` : '-',
        color: tint(row.epcTone),
      };
    }
    case 'Profit': {
      if (row.profit) {
        return { value: row.profit, color: tint(row.profitTone) };
      }
      const profit = revenue - spend;
      return { value: `$${profit.toLocaleString()}`, color: tint(row.profitTone) };
    }
    case 'ROI':
      return {
        value:
          row.roi ??
          (spend ? `${(((revenue - spend) / spend) * 100).toFixed(2)}%` : '-'),
        color: tint(row.roiTone),
      };
    case 'CTR':
      return { value: '5.67%' };
    case 'CPM': {
      const v = typeof row.visits === 'number' ? row.visits : fallbackVisits;
      return { value: `$${((spend / v) * 1000).toFixed(2)}` };
    }
    default:
      return { value: '-' };
  }
};

// Renders a single <td> for a given metric. Applies tone color and weight
// consistently so every dynamic column looks the same across tables.
export const MetricCellTd = ({
  row,
  metric,
  weight = 600,
  size,
}: {
  row: MetricSource;
  metric: string;
  weight?: number;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}) => {
  const cell = getMetricCell(row, metric);
  return (
    <td>
      <Text
        weight={weight}
        size={size}
        sx={cell.color ? { color: cell.color } : undefined}
      >
        {cell.value}
      </Text>
    </td>
  );
};

// Renders the Grade / Grade Reason cells from the backend-computed row verdict.
export const GradeCellTd = ({
  grade,
  reason,
  column,
  tdStyle,
}: {
  grade?: string | null;
  reason?: string;
  column: string;
  tdStyle?: React.CSSProperties;
}) => {
  const theme = useMantineTheme();
  const isGrade = grade === 'good' || grade === 'watch' || grade === 'poor';
  if (column === 'Grade Reason') {
    return (
      <td style={tdStyle}>
        <Text size="sm" color="dimmed" sx={{ whiteSpace: 'nowrap' }}>
          {reason || '—'}
        </Text>
      </td>
    );
  }
  if (!isGrade) {
    return (
      <td style={tdStyle}>
        <Text size="sm" color="dimmed">—</Text>
      </td>
    );
  }
  const c = gradeColors(grade as Grade, theme.colorScheme === 'dark');
  return (
    <td style={tdStyle}>
      <Flex align="center" sx={{ whiteSpace: 'nowrap' }}>
        <LegendDot color={c.dot} />
        <Text weight={600} size="sm" sx={{ color: c.text }}>
          {GRADE_LABEL[grade as Grade]}
        </Text>
      </Flex>
    </td>
  );
};

const pctTone = (pct: number): Tone =>
  pct >= 60 ? 'good' : pct >= 50 ? 'warning' : 'poor';

type CohortGoalStats = {
  conv: string;
  convPct: string;
  convPctTone: Tone;
  cost: string;
  avgDays: string;
};

type CohortSummary = {
  key: string;
  label: string;
  spend: string;
  users: string;
  book: CohortGoalStats;
  show: CohortGoalStats;
  purchase: CohortGoalStats;
};

const buildCohortSummary = (
  key: string,
  label: string,
  rows: LifecycleRow[]
): CohortSummary => {
  const sumSpend = rows.reduce((a, r) => a + parseNum(r.spend), 0);
  const sumUsers = rows.reduce((a, r) => a + parseNum(r.users), 0);

  const aggregate = (goal: 'book' | 'show' | 'purchase'): CohortGoalStats => {
    const conv = rows.reduce((a, r) => a + parseNum(r[goal].conv), 0);
    const pct = sumUsers ? (conv / sumUsers) * 100 : 0;
    const avgDays = rows.length
      ? rows.reduce((a, r) => a + parseNum(r[goal].avgDays), 0) / rows.length
      : 0;
    const cost = conv ? sumSpend / conv : 0;
    return {
      conv: conv.toLocaleString(),
      convPct: `${pct.toFixed(2)}%`,
      convPctTone: pctTone(pct),
      cost: `$${cost.toFixed(2)}`,
      avgDays: `${avgDays.toFixed(2)}d`,
    };
  };

  return {
    key,
    label,
    spend: `$${sumSpend.toLocaleString()}`,
    users: sumUsers.toLocaleString(),
    book: aggregate('book'),
    show: aggregate('show'),
    purchase: aggregate('purchase'),
  };
};

// Explains how conversions are credited for the current campaign selection and attribution
// model. Shown in the report header and above the Funnel tab's campaign list.
export const AttributionHelp = ({
  multiCampaign,
  attribution,
}: {
  multiCampaign: boolean;
  attribution: string;
}) => {
  // First / Last Click pick one winning campaign per conversion; Full Impact and Same Day do not.
  const singleWinner = attribution === 'first_click' || attribution === 'last_click';
  const title = multiCampaign ? 'Global Attribution' : 'Funnel Attribution';

  let body: string;
  if (!multiCampaign) {
    body = "One campaign is selected, so conversions are credited inside that campaign's own funnel. "
      + 'The campaign is credited for every conversion it took part in.';
  } else if (singleWinner) {
    body = 'Each conversion is credited to only one campaign - the true '
      + (attribution === 'first_click' ? 'first' : 'last')
      + " click across the customer's complete journey - so nothing is double-counted, and it shows "
      + "here when that campaign is one you've included. Totals can therefore be lower than the same "
      + 'campaigns viewed one at a time, where every campaign is credited for every conversion it took part in.';
  } else {
    body = (attribution === 'full_impact' ? 'Full Impact' : 'Same Day')
      + ' credits every campaign that took part in the journey, so one conversion can appear under more '
      + 'than one campaign and the rows will not add up to the total. Choose First Click or Last Click '
      + 'to credit each conversion to a single campaign.';
  }

  return (
    <Popover width={320} position="bottom" withArrow withinPortal shadow="md" middlewares={{ flip: false, shift: false }}>
      <Popover.Target>
        <ActionIcon variant="light" color="blue" size="md" radius="sm" title={title}>
          <IconHelp size="1.1rem" />
        </ActionIcon>
      </Popover.Target>
      <Popover.Dropdown>
        <Flex align="center" gap="xs" mb={6}>
          {multiCampaign ? <IconWorld size={18} /> : <IconFilter size={18} />}
          <Text size="sm" fw={600}>{title}</Text>
        </Flex>
        <Text size="sm">{body}</Text>
      </Popover.Dropdown>
    </Popover>
  );
};

export const LegendDot = ({ color }: { color: string }) => (
  <Box
    sx={{
      display: 'inline-block',
      width: 10,
      height: 10,
      borderRadius: '50%',
      backgroundColor: color,
      marginRight: 6,
    }}
  />
);

export const SectionHeader = ({
  icon,
  label,
  rightNode,
  actionNode,
  opened,
  toggle,
}: {
  icon: React.ReactNode;
  label: string;
  rightNode?: React.ReactNode;
  // A control on the right edge; it sits outside the toggle button because a button cannot hold inputs.
  actionNode?: React.ReactNode;
  opened: boolean;
  toggle: () => void;
}) => {
  const title = (
    <Group spacing="xs">
      {icon}
      <Text weight={600} size="sm" transform="uppercase" sx={{ letterSpacing: 0.5 }}>
        {label}
      </Text>
      {rightNode}
    </Group>
  );
  const chevron = opened ? <IconChevronUp size="1rem" /> : <IconChevronDown size="1rem" />;

  if (actionNode) {
    return (
      <Flex
        align="center"
        gap="sm"
        sx={(theme) => ({
          borderBottom: `1px solid ${
            theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]
          }`,
        })}
      >
        <UnstyledButton
          onClick={toggle}
          aria-expanded={opened}
          sx={{ flex: 1, display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '10px 4px' }}
        >
          {title}
        </UnstyledButton>
        {actionNode}
        <ActionIcon variant="subtle" size="sm" onClick={toggle} aria-label={opened ? 'Collapse' : 'Expand'}>
          {chevron}
        </ActionIcon>
      </Flex>
    );
  }

  return (
    // UnstyledButton rather than a clickable Flex: the whole row is the target,
    // and screen readers get real button semantics plus aria-expanded. The
    // chevron below renders as component="div" so it keeps the ActionIcon look
    // without nesting a second <button> inside this one.
    <UnstyledButton
      onClick={toggle}
      aria-expanded={opened}
      sx={(theme) => ({
        width: '100%',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        cursor: 'pointer',
        padding: '10px 4px',
        borderBottom: `1px solid ${
          theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]
        }`,
      })}
    >
      {title}
      <ActionIcon component="div" variant="subtle" size="sm">
        {chevron}
      </ActionIcon>
    </UnstyledButton>
  );
};

// Device / Hour (and Source on the Total tab) split for a Goal Breakdown; the same setting its tab's Settings drawer edits.
export const GoalBreakdownTypeSelect = <T extends 'none' | 'device' | 'hour' | 'source'>({
  value,
  onChange,
  withSource = false,
}: {
  value: T;
  onChange: (v: T) => void;
  withSource?: boolean;
}) => (
  // Same label + teal SegmentedControl pairing as Lifecycle's Group by.
  <Flex align="center" gap="xs">
    <Text size="sm" color="dimmed">
      Breakdown
    </Text>
    <SegmentedControl
      value={value}
      onChange={(v) => onChange(v as T)}
      color="teal"
      size="xs"
      data={[
        { label: 'None', value: 'none' },
        { label: 'Device', value: 'device' },
        { label: 'Hour', value: 'hour' },
        ...(withSource ? [{ label: 'Source', value: 'source' }] : []),
      ]}
    />
  </Flex>
);

// Ad network campaigns hidden from the tables, keyed so a campaign's ad sets and ads share its key.
export const campaignHideKey = (row: { source: string; id: string; parent_campaign_id?: string }, level: string): string => {
  const campaignId = level === 'campaign' ? row.id : (row.parent_campaign_id ?? '');
  return campaignId !== '' ? `${row.source}:${campaignId}` : '';
};

// Table sx: the hide icon shows on row hover (always on touch screens and on hidden rows).
export const CAMPAIGN_HIDE_SX = {
  '& .vo-campaign-hide': { opacity: 0, transition: 'opacity 120ms ease' },
  '& tr:hover .vo-campaign-hide, & .vo-campaign-hide:focus-visible': { opacity: 1 },
  '@media (hover: none)': { '& .vo-campaign-hide': { opacity: 1 } },
};

// Hide or unhide a campaign from its row; same icon button pattern as the journey icon.
export const CampaignHideToggle = ({ hidden, onToggle }: { hidden: boolean; onToggle: () => void }) => (
  <Tooltip label={hidden ? 'Unhide campaign' : 'Hide campaign'} withArrow withinPortal>
    <ActionIcon
      size="md"
      variant="subtle"
      color="gray"
      className={hidden ? undefined : 'vo-campaign-hide'}
      aria-label={hidden ? 'Unhide campaign' : 'Hide campaign'}
      onClick={(e: React.MouseEvent) => { e.stopPropagation(); onToggle(); }}
      sx={{ flex: '0 0 auto' }}
    >
      {hidden ? <IconEye size="1.15rem" stroke={1.8} /> : <IconEyeOff size="1.15rem" stroke={1.8} />}
    </ActionIcon>
  </Tooltip>
);

// Divider row that keeps hidden campaigns collapsed at the foot of a table.
export const HiddenRowsToggle = ({
  count,
  open,
  onToggle,
  colSpan,
  borderColor,
}: {
  count: number;
  open: boolean;
  onToggle: () => void;
  colSpan: number;
  borderColor: string;
}) => (
  <tr>
    <td colSpan={colSpan} style={{ borderTop: `2px solid ${borderColor}`, padding: '8px 12px' }}>
      <UnstyledButton onClick={onToggle} aria-expanded={open} sx={{ position: 'sticky', left: 12 }}>
        <Flex align="center" gap={6}>
          {open ? <IconChevronDown size="0.9rem" /> : <IconChevronRight size="0.9rem" />}
          <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
            Hidden ({count})
          </Text>
        </Flex>
      </UnstyledButton>
    </td>
  </tr>
);

// The one entry point to customer journeys on every tab: same icon, pointer and tooltip everywhere.
export const JourneyIconButton = ({ onClick }: { onClick: () => void }) => (
  <Tooltip label="View customer journeys" withArrow withinPortal>
    <ActionIcon size="xs" variant="subtle" color="gray" aria-label="View customer journeys" onClick={onClick}>
      <IconRoute size="0.85rem" />
    </ActionIcon>
  </Tooltip>
);

export const GoalVariationBreakdown = ({
  goalRows,
  metrics,
  customerType,
  onVariationOpen,
  onVariationJourneyOpen,
  onVariationConversionLogOpen,
  onConversionLogOpen,
}: {
  goalRows: GoalRow[];
  metrics: string[];
  /* Goal level, not variation level: the goal's own conversions open the whole goal's log, and
     at 0 the undefined ones - an undefined conversion has no landing visit, so it has no variation. */
  onConversionLogOpen?: (row: GoalRow, kind?: 'undefined') => void;
  // Optional: makes the variation name open the Variation Details modal.
  onVariationOpen?: (goal: GoalRow, variation: any) => void;
  // Optional: makes the variation's Conversions cell open the Customer Journey drawer.
  onVariationJourneyOpen?: (goal: GoalRow, variation: any) => void;
  // Optional: makes the variation's Conversions count open the Conversion Log for that variation.
  onVariationConversionLogOpen?: (goal: GoalRow, variation: any) => void;
  // Same contract as GoalBreakdownTable: this component owns no state and no fetching, so the
  // goal table and the variation table cannot behave differently.
  customerType?: {
    isOpen: (row: GoalRow) => boolean;
    isLoading: (row: GoalRow) => boolean;
    rowsFor: (row: GoalRow) => any[] | undefined;
    errorFor: (row: GoalRow) => boolean;
    onToggle: (row: GoalRow) => void;
  };
}) => {
  const theme = useMantineTheme();
  const headerBg =
    theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[1];
  const panelBorder =
    theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3];

  const variationGoalRows = goalRows.filter(
    (g) => ((g.goal_key ?? '') as string).indexOf('button:') !== 0
  );

  return (
    <Stack spacing="md">
      {variationGoalRows.map((goal) => {
        const variations = Array.isArray(goal.variations) ? goal.variations : [];
        return (
          <Box
            key={goal.goal_key ?? goal.goal}
            sx={{
              border: `1px solid ${panelBorder}`,
              borderRadius: 6,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                backgroundColor: headerBg,
                padding: '10px 14px',
                borderBottom: `1px solid ${panelBorder}`,
              }}
            >
              <Flex align="center" justify="space-between" gap="sm">
                <Text weight={600} size="sm">
                  {goal.goal}
                </Text>
                {!!onConversionLogOpen && (
                  <Flex align="center" gap={6}>
                    <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                      Conversions
                    </Text>
                    <Tooltip label="Open conversion log" withArrow withinPortal>
                      <UnstyledButton
                        onClick={() => onConversionLogOpen(
                          goal,
                          goal.conversions > 0 ? undefined : ((goal.undefined_excluded ?? 0) > 0 ? 'undefined' : undefined)
                        )}
                        sx={{
                          fontWeight: 600,
                          fontSize: 14,
                          cursor: 'pointer',
                          textDecoration: goal.conversions > 0 ? 'underline' : 'none',
                          textUnderlineOffset: 3,
                        }}
                      >
                        {goal.conversions.toLocaleString()}
                      </UnstyledButton>
                    </Tooltip>
                  </Flex>
                )}
              </Flex>
            </Box>
            <Table verticalSpacing="sm" horizontalSpacing="md">
              <thead>
                <tr>
                  <th>
                    <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                      Variation
                    </Text>
                  </th>
                  {metrics.map((m) => (
                    <th key={m}>
                      <Flex align="center" gap={4} sx={{ display: 'inline-flex' }}>
                        <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                          {m === 'CPA' ? 'Estimated CPA' : m}
                        </Text>
                        {m === 'CPA' && (
                          <Tooltip
                            label="Blended from total campaign spend. For comparing versions only - not actual CPA."
                            withArrow
                            multiline
                            width={330}
                            position="right"
                          >
                            <ActionIcon variant="subtle" size="xs" color="gray" aria-label="Estimated CPA info">
                              <IconInfoCircle size={13} />
                            </ActionIcon>
                          </Tooltip>
                        )}
                      </Flex>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {variations.length === 0 ? (
                  <tr>
                    <td colSpan={1 + metrics.length}>
                      <Text size="xs" color="dimmed" align="center" py="sm">
                        No test variations recorded for this goal in the selected time range.
                      </Text>
                    </td>
                  </tr>
                ) : (
                  variations.map((row) => {
                    /* Eligibility is decided per variation by the report - a purchase webhook
                       with conversions of its own - so a variation with none offers no link. */
                    const ctEligible = !!customerType && row.customer_type_available === 1;
                    const ctOpen = ctEligible && customerType!.isOpen(row);
                    const ctRows = ctOpen ? customerType!.rowsFor(row) : undefined;
                    return (
                    <React.Fragment key={row.variation_id || row.variation_name}>
                    <tr>
                      <td>
                        {onVariationOpen ? (
                          <UnstyledButton
                            onClick={() => onVariationOpen(goal, row)}
                            title="Variation Details"
                            sx={{ fontWeight: 600, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 3 }}
                          >
                            {row.variation_name}
                          </UnstyledButton>
                        ) : (
                          <Text weight={600}>{row.variation_name}</Text>
                        )}
                        {ctEligible && (
                          <Anchor
                            component="button"
                            type="button"
                            fz={11}
                            fw={500}
                            underline={false}
                            aria-expanded={ctOpen}
                            aria-label={`${ctOpen ? 'Hide' : 'Show'} Customer Type for ${row.variation_name}`}
                            disabled={customerType!.isLoading(row)}
                            onClick={() => customerType!.onToggle(row)}
                            style={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: 2 }}
                          >
                            {ctOpen ? <IconChevronDown size="0.75rem" /> : <IconChevronRight size="0.75rem" />}
                            {customerType!.isLoading(row) ? 'Loading…' : 'Customer Type'}
                          </Anchor>
                        )}
                      </td>
                      {metrics.map((m) => {
                        const loggable = m === 'Conversions' && (goal.goal_key ?? '').indexOf('button:') !== 0;
                        const hasConversions = Number(row.conversions) > 0;
                        const journeyable = loggable && hasConversions && !!onVariationJourneyOpen;
                        // The log opens at 0 too; only a non-zero count is underlined.
                        const logClickable = loggable && !!onVariationConversionLogOpen;
                        if (!journeyable && !logClickable) {
                          return <MetricCellTd key={m} row={row} metric={m} />;
                        }
                        const cell = getMetricCell(row, m);
                        return (
                          <td key={m}>
                            <Flex align="center" gap={6}>
                              {logClickable ? (
                                <Tooltip label="Open conversion log" withArrow withinPortal>
                                  <UnstyledButton
                                    onClick={() => onVariationConversionLogOpen!(goal, row)}
                                    sx={{
                                      fontWeight: 600,
                                      cursor: 'pointer',
                                      textDecoration: hasConversions ? 'underline' : 'none',
                                      textUnderlineOffset: 3,
                                      ...(cell.color ? { color: cell.color } : {}),
                                    }}
                                  >
                                    {cell.value}
                                  </UnstyledButton>
                                </Tooltip>
                              ) : (
                                <Text weight={600} sx={cell.color ? { color: cell.color } : undefined}>
                                  {cell.value}
                                </Text>
                              )}
                              {journeyable && <JourneyIconButton onClick={() => onVariationJourneyOpen!(goal, row)} />}
                            </Flex>
                          </td>
                        );
                      })}
                      {metrics.length === 0 && (
                        <td>
                          <Text size="xs" color="dimmed">
                            No metrics selected
                          </Text>
                        </td>
                      )}
                    </tr>
                    {ctOpen && Array.isArray(ctRows)
                      && (ctRows.length > 0 ? ctRows : customerTypeZeroRows(row)).map((ct: any, ci: number) => (
                      <tr key={`ct-${row.variation_id || row.variation_name}-${ci}`}>
                        <td>
                          <Text size="xs" italic pl={18}>&#8627; {ct.customer_type_name}</Text>
                        </td>
                        {metrics.map((m) => (
                          <MetricCellTd key={m} row={ct} metric={m} />
                        ))}
                        {metrics.length === 0 && <td />}
                      </tr>
                    ))}
                    {/* This variation's own device / hour rows, formatted by the same backend function. */}
                    {((row.device_breakdown?.length ? row.device_breakdown : row.hour_breakdown ?? []) as any[]).map((seg: any, si: number) => (
                      <tr key={`vseg-${row.variation_id || row.variation_name}-${si}`} style={{ background: 'rgba(0,0,0,0.025)' }}>
                        <td>
                          <Text size="xs" italic pl={18}>&#8627; {seg.goal}</Text>
                        </td>
                        {metrics.map((m) => (
                          <MetricCellTd key={m} row={seg} metric={m} />
                        ))}
                        {metrics.length === 0 && <td />}
                      </tr>
                    ))}
                    </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </Table>
          </Box>
        );
      })}
    </Stack>
  );
};

// Maps a metric display label to the GoalRow field that holds its sortable value.
const goalRowSortValue = (row: GoalRow, field: string): unknown => {
  if (field === 'goal') { return row.goal; }
  const k = field as keyof GoalRow;
  return (row as any)[k];
};
const METRIC_TO_GOAL_FIELD: Record<string, string> = {
  Grade: 'grade_rank', 'Grade Reason': 'grade_reason',
  Spend: 'spend', Revenue: 'revenue', Conversions: 'conversions',
  CPA: 'cpa', ROAS: 'roas', Uniques: 'uniques', Visits: 'visits',
  UCR: 'ucr', CR: 'cr', AOV: 'aov', EPC: 'epc', Profit: 'profit', ROI: 'roi',
  'Unique EPC': 'arpu', ACV: 'acv',
};

/* Customer Type always has the same two segments, so the breakdown can be drawn from the labels
   alone. When the report sends no rows for an eligible goal - nothing purchased yet, or a backend
   that predates the zero-filled response - these stand in, reading zero rather than a "no data"
   message. Definedness is inherited from the goal row above: a metric that row could not compute
   (no spend, no uniques) stays "-" here too, so a segment never contradicts its parent. */
const CT_SEGMENT_LABELS = ['First-time Customers', 'Returning Customers'];
const ctZeroLike = (parentValue: any, zero: string) =>
  (parentValue === '-' || parentValue === undefined || parentValue === null) ? '-' : zero;
const customerTypeZeroRows = (parent: any) =>
  CT_SEGMENT_LABELS.map((name) => ({
    customer_type_name: name,
    conversions: 0,
    /* Spend is the goal's own and is not divisible between the segments, so it is carried across
       unchanged - exactly what a real segment row from the report carries. */
    spend: parent?.spend,
    uniques: parent?.uniques,
    visits: parent?.visits,
    revenue: ctZeroLike(parent?.revenue, '$0.00'),
    acv:     ctZeroLike(parent?.acv,     '$0.00'),
    arpu:    ctZeroLike(parent?.arpu,    '$0.00'),
    ucr:     ctZeroLike(parent?.ucr,     '0.00%'),
    cr:      ctZeroLike(parent?.cr,      '0.00%'),
    roas:    ctZeroLike(parent?.roas,    '0.00x'),
    /* Undefined with no conversions to divide by - the same "-" the goal row shows at zero. */
    cpa: '-',
    aov: '-',
    epc: '-',
  }));

export const GoalBreakdownTable = ({
  goalRows,
  metrics,
  onJourneyOpen,
  onConversionLogOpen,
  customerType,
}: {
  goalRows: GoalRow[];
  metrics: string[];
  // Optional: when provided, the Conversions cell shows a journey icon for rows with
  // conversions > 0 that opens the customer journey drawer for that goal.
  onJourneyOpen?: (row: GoalRow) => void;
  // Optional: when provided, the Conversions number itself opens the conversion log, even at 0.
  // 'undefined' opens it on the undefined conversions, used when the count is 0 but some exist.
  onConversionLogOpen?: (row: GoalRow, kind?: 'undefined') => void;
  // Optional Customer Type breakdown, driven entirely by the caller: this table renders the
  // link and the rows but owns no fetching and no state, so both tabs share one implementation.
  customerType?: {
    isOpen: (row: GoalRow) => boolean;
    isLoading: (row: GoalRow) => boolean;
    rowsFor: (row: GoalRow) => any[] | undefined;
    errorFor: (row: GoalRow) => boolean;
    onToggle: (row: GoalRow) => void;
  };
}) => {
  const { sort, toggle } = useTableSort();
  const sortedRows = useMemo(
    () => sortRows(goalRows, sort, goalRowSortValue),
    [goalRows, sort],
  );
  const renderConversionCell = (row: GoalRow, key: string) => {
    const hasConversions = row.conversions > 0;
    // Button goals record hits in visitor_button_tracking, so they have no conversion rows to list.
    const isButtonGoal = (row.goal_key ?? '').indexOf('button:') === 0;
    const logClickable = !!onConversionLogOpen && !isButtonGoal;
    const journeyClickable = !!onJourneyOpen && hasConversions && !isButtonGoal;
    if (!logClickable && !journeyClickable) {
      return <MetricCellTd key={key} row={row} metric="Conversions" />;
    }
    // Clickable cells still carry the benchmark tint; only the click target differs.
    const cell = getMetricCell(row, 'Conversions');
    return (
      <td key={key}>
        <Flex align="center" gap={6}>
          {logClickable ? (
            <Tooltip label="Open conversion log" withArrow withinPortal>
              <UnstyledButton
                // At 0, land on the undefined conversions when this goal has any.
                onClick={() => onConversionLogOpen && onConversionLogOpen(
                  row,
                  !hasConversions && (row.undefined_excluded ?? 0) > 0 ? 'undefined' : undefined
                )}
                sx={{
                  fontWeight: 600,
                  cursor: 'pointer',
                  textDecoration: hasConversions ? 'underline' : 'none',
                  textUnderlineOffset: 3,
                  ...(cell.color ? { color: cell.color } : {}),
                }}
              >
                {cell.value}
              </UnstyledButton>
            </Tooltip>
          ) : (
            <Text weight={600} sx={cell.color ? { color: cell.color } : undefined}>
              {cell.value}
            </Text>
          )}
          {journeyClickable && <JourneyIconButton onClick={() => onJourneyOpen && onJourneyOpen(row)} />}
        </Flex>
      </td>
    );
  };
  return (
    <>
      <Table verticalSpacing="sm" horizontalSpacing="md">
        <thead>
          <tr>
            <SortableTh field="goal" sort={sort} onToggle={toggle}>Goal</SortableTh>
            {metrics.map((m) => (
              <SortableTh
                key={m}
                field={METRIC_TO_GOAL_FIELD[m] ?? m}
                sort={sort}
                onToggle={toggle}
              >
                {m}
              </SortableTh>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((row) => {
            /* The report decides eligibility server-side - a webhook goal whose webhook counts
               as a purchase, with conversions to split - so this table never re-applies the rule. */
            const ctEligible = !!customerType && row.customer_type_available === 1;
            const ctOpen     = ctEligible && customerType!.isOpen(row);
            const ctRows     = ctOpen ? customerType!.rowsFor(row) : undefined;
            const ctError    = ctOpen && customerType!.errorFor(row);
            const colCount   = 1 + (metrics.length === 0 ? 1 : metrics.length);
            return (
            <React.Fragment key={row.goal}>
            <tr>
              <td>
                <Text weight={600}>{row.goal}</Text>
                {ctEligible && (
                  <Anchor
                    component="button"
                    type="button"
                    fz={11}
                    fw={500}
                    underline={false}
                    aria-expanded={ctOpen}
                    aria-label={`${ctOpen ? 'Hide' : 'Show'} Customer Type breakdown for ${row.goal}`}
                    disabled={customerType!.isLoading(row)}
                    onClick={() => customerType!.onToggle(row)}
                    style={{ display: 'flex', alignItems: 'center', gap: 2, marginTop: 2 }}
                  >
                    {ctOpen ? <IconChevronDown size="0.75rem" /> : <IconChevronRight size="0.75rem" />}
                    {customerType!.isLoading(row) ? 'Loading…' : 'Customer Type'}
                  </Anchor>
                )}
              </td>
              {metrics.map((m) =>
                m === 'Conversions'
                  ? renderConversionCell(row, m)
                  : m === 'Grade' || m === 'Grade Reason'
                  ? <GradeCellTd key={m} grade={row.grade} reason={row.grade_reason} column={m} />
                  : <MetricCellTd key={m} row={row} metric={m} />
              )}
              {metrics.length === 0 && (
                <td>
                  <Text size="xs" color="dimmed">
                    No metrics selected
                  </Text>
                </td>
              )}
            </tr>
            {ctOpen && ctError && (
              <tr>
                <td colSpan={colCount}>
                  <Text size="xs" color="dimmed" italic pl={18}>Customer Type could not be loaded.</Text>
                </td>
              </tr>
            )}
            {ctOpen && !ctError && Array.isArray(ctRows)
              && (ctRows.length > 0 ? ctRows : customerTypeZeroRows(row)).map((ct: any, ci: number) => (
              <tr key={`ct-${row.goal}-${ci}`}>
                <td>
                  <Text size="xs" italic pl={18}>&#8627; {ct.customer_type_name}</Text>
                </td>
                {/* The segment rows come back shaped exactly like the goal row, formatted by the
                    same backend function, so the same metric cell renders them. Metrics that are
                    not attributable per segment (Profit, ROI - campaign spend cannot be divided
                    between first-time and returning buyers) are simply absent and read blank. */}
                {metrics.map((m) => (
                  <MetricCellTd key={m} row={ct} metric={m} />
                ))}
                {metrics.length === 0 && <td />}
              </tr>
            ))}
            {/* Device / Hour / Source segments follow their goal; they are formatted by the same backend function. */}
            {(row.device_breakdown?.length ? row.device_breakdown : row.hour_breakdown?.length ? row.hour_breakdown : row.source_breakdown ?? []).map((seg, si) => (
              <tr key={`seg-${row.goal}-${si}`} style={{ background: 'rgba(0,0,0,0.025)' }}>
                <td>
                  <Text size="xs" italic pl={18}>&#8627; {seg.goal}</Text>
                </td>
                {metrics.map((m) =>
                  m === 'Grade' || m === 'Grade Reason'
                    ? <GradeCellTd key={m} grade={seg.grade} reason={seg.grade_reason} column={m} />
                    : <MetricCellTd key={m} row={seg} metric={m} />
                )}
                {metrics.length === 0 && <td />}
              </tr>
            ))}
            </React.Fragment>
            );
          })}
        </tbody>
      </Table>
      <Flex gap="lg" mt="sm" align="center">
        <Text size="xs">
          <LegendDot color="#22c55e" /> Good
        </Text>
        <Text size="xs">
          <LegendDot color="#f59e0b" /> Warning
        </Text>
        <Text size="xs">
          <LegendDot color="#ef4444" /> Poor
        </Text>
      </Flex>
    </>
  );
};

export const LifecycleBody = ({
  lifecycleRows,
  lifecycleGroupBy,
  setLifecycleGroupBy,
}: {
  lifecycleRows: LifecycleRow[];
  lifecycleGroupBy: string;
  setLifecycleGroupBy: (v: string) => void;
}) => {
  const theme = useMantineTheme();

  const cellBorderColor =
    theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3];
  const goalDividerColor =
    theme.colorScheme === 'dark' ? theme.colors.dark[1] : theme.colors.gray[4];
  const goalDividerStyle: React.CSSProperties = {
    borderLeft: `1px solid ${goalDividerColor}`,
  };
  const headerBg =
    theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[1];
  const summaryBg =
    theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.colors.gray[1];

  const cohortSummaries = useMemo<CohortSummary[]>(() => {
    if (lifecycleRows.length === 0) return [];
    const half = Math.ceil(lifecycleRows.length / 2);
    const first = lifecycleRows.slice(0, half);
    const last = lifecycleRows.slice(half);
    return [
      buildCohortSummary('all', 'Selected Date Range (Summary)', lifecycleRows),
      buildCohortSummary('first', 'Last 7 Days (Summary)', first),
      buildCohortSummary('last', 'Last 30 Days (Summary)', last),
    ];
  }, [lifecycleRows]);

  const totalColumns = 15; // 3 fixed (Entry/Spend/Users) + 3 goals × 4 cols

  return (
    <Paper
      withBorder
      radius="md"
      p="md"
      mt="sm"
      sx={(t) => ({
        backgroundColor: t.colorScheme === 'dark' ? t.colors.dark[6] : t.colors.gray[0],
      })}
    >
      <Flex align="center" gap="md" mb="md" wrap="wrap">
        <Text size="sm" color="dimmed">
          Group by
        </Text>
        <SegmentedControl
          value={lifecycleGroupBy}
          onChange={setLifecycleGroupBy}
          color="teal"
          size="xs"
          data={[
            { label: 'Daily', value: 'daily' },
            { label: 'Weekday vs Weekend', value: 'weekend' },
          ]}
        />
        <Text size="xs" color="dimmed">
          Click{' '}
          <Text component="span" weight={600}>
            Cost/Goal
          </Text>{' '}
          to see contributing ads
        </Text>
      </Flex>

      <ScrollArea type="auto" offsetScrollbars>
        <Table
          verticalSpacing="sm"
          horizontalSpacing="md"
          withBorder
          sx={{
            minWidth: 1300,
            borderColor: cellBorderColor,
            'thead tr th': {
              backgroundColor: headerBg,
              borderColor: cellBorderColor,
              whiteSpace: 'nowrap',
            },
            'tbody tr td': { borderColor: cellBorderColor },
          }}
        >
          <thead>
            <tr>
              <th />
              <th />
              <th />
              <th colSpan={4} style={goalDividerStyle}>
                <Text
                  size="xs"
                  color="teal"
                  transform="uppercase"
                  weight={700}
                  align="center"
                >
                  Book Call
                </Text>
              </th>
              <th colSpan={4} style={goalDividerStyle}>
                <Text
                  size="xs"
                  transform="uppercase"
                  weight={700}
                  align="center"
                  sx={{ color: '#f59e0b' }}
                >
                  Show
                </Text>
              </th>
              <th colSpan={4} style={goalDividerStyle}>
                <Text
                  size="xs"
                  color="blue"
                  transform="uppercase"
                  weight={700}
                  align="center"
                >
                  Purchase
                </Text>
              </th>
            </tr>
            <tr>
              <th>
                <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                  Entry Date
                </Text>
              </th>
              <th>
                <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                  Total Spend
                </Text>
              </th>
              <th>
                <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                  Users
                </Text>
              </th>
              <th style={goalDividerStyle}>
                <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                  Conv.
                </Text>
              </th>
              <th>
                <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                  Conv %
                </Text>
              </th>
              <th>
                <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                  Cost/Goal
                </Text>
              </th>
              <th>
                <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                  Avg Days
                </Text>
              </th>
              <th style={goalDividerStyle}>
                <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                  Conv.
                </Text>
              </th>
              <th>
                <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                  Conv %
                </Text>
              </th>
              <th>
                <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                  Cost/Goal
                </Text>
              </th>
              <th>
                <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                  Avg Days
                </Text>
              </th>
              <th style={goalDividerStyle}>
                <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                  Conv.
                </Text>
              </th>
              <th>
                <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                  Conv %
                </Text>
              </th>
              <th>
                <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                  Cost/Goal
                </Text>
              </th>
              <th>
                <Text size="xs" color="dimmed" transform="uppercase" weight={600}>
                  Avg Days
                </Text>
              </th>
            </tr>
          </thead>
          <tbody>
            {lifecycleRows.map((row) => (
              <tr key={row.entry}>
                <td>
                  <Text weight={600}>{row.entry}</Text>
                </td>
                <td>{row.spend}</td>
                <td>{row.users}</td>
                <td style={goalDividerStyle}>
                  <Text color="teal" weight={600}>
                    {row.book.conv}
                  </Text>
                </td>
                <td>
                  <Text sx={{ color: toneColor(row.book.convPctTone) }} weight={600}>
                    {row.book.convPct}
                  </Text>
                </td>
                <td>
                  <UnstyledButton
                    sx={(t) => ({
                      border: `1px solid ${
                        t.colorScheme === 'dark' ? t.colors.dark[3] : t.colors.gray[4]
                      }`,
                      borderRadius: 4,
                      padding: '2px 8px',
                      fontSize: 12,
                    })}
                  >
                    {row.book.cost}
                  </UnstyledButton>
                </td>
                <td>{row.book.avgDays}</td>
                <td style={goalDividerStyle}>
                  <Text sx={{ color: '#f59e0b' }} weight={600}>
                    {row.show.conv}
                  </Text>
                </td>
                <td>
                  <Text sx={{ color: toneColor(row.show.convPctTone) }} weight={600}>
                    {row.show.convPct}
                  </Text>
                </td>
                <td>
                  <UnstyledButton
                    sx={(t) => ({
                      border: `1px solid ${
                        t.colorScheme === 'dark' ? t.colors.dark[3] : t.colors.gray[4]
                      }`,
                      borderRadius: 4,
                      padding: '2px 8px',
                      fontSize: 12,
                    })}
                  >
                    {row.show.cost}
                  </UnstyledButton>
                </td>
                <td>{row.show.avgDays}</td>
                <td style={goalDividerStyle}>
                  <Text color="blue" weight={600}>
                    {row.purchase.conv}
                  </Text>
                </td>
                <td>
                  <Text sx={{ color: toneColor(row.purchase.convPctTone) }} weight={600}>
                    {row.purchase.convPct}
                  </Text>
                </td>
                <td>
                  <UnstyledButton
                    sx={(t) => ({
                      border: `1px solid ${
                        t.colorScheme === 'dark' ? t.colors.dark[3] : t.colors.gray[4]
                      }`,
                      borderRadius: 4,
                      padding: '2px 8px',
                      fontSize: 12,
                    })}
                  >
                    {row.purchase.cost}
                  </UnstyledButton>
                </td>
                <td>{row.purchase.avgDays}</td>
              </tr>
            ))}

            {cohortSummaries.length > 0 && (
              <>
                <tr>
                  <td
                    colSpan={totalColumns}
                    style={{
                      backgroundColor: summaryBg,
                      padding: '12px 16px',
                      borderTop: `2px solid ${goalDividerColor}`,
                    }}
                  >
                    <Text
                      size="xs"
                      weight={700}
                      transform="uppercase"
                      sx={{ letterSpacing: 0.8 }}
                    >
                      Cohort Summaries
                    </Text>
                  </td>
                </tr>
                {cohortSummaries.map((s) => (
                  <tr key={s.key} style={{ backgroundColor: summaryBg }}>
                    <td>
                      <Text weight={600} sx={{ color: '#1c7ed6' }}>{s.label}</Text>
                    </td>
                    <td>
                      <Text>{s.spend}</Text>
                    </td>
                    <td>
                      <Text>{s.users}</Text>
                    </td>
                    <td style={goalDividerStyle}>
                      <Text color="teal" weight={700}>
                        {s.book.conv}
                      </Text>
                    </td>
                    <td>
                      <Text
                        weight={700}
                        sx={{ color: toneColor(s.book.convPctTone) }}
                      >
                        {s.book.convPct}
                      </Text>
                    </td>
                    <td>
                      <UnstyledButton
                        sx={(t) => ({
                          border: `1px solid ${
                            t.colorScheme === 'dark' ? t.colors.dark[3] : t.colors.gray[4]
                          }`,
                          borderRadius: 4,
                          padding: '2px 8px',
                          fontSize: 12,
                        })}
                      >
                        {s.book.cost}
                      </UnstyledButton>
                    </td>
                    <td>
                      <Text>{s.book.avgDays}</Text>
                    </td>
                    <td style={goalDividerStyle}>
                      <Text sx={{ color: '#f59e0b' }} weight={700}>
                        {s.show.conv}
                      </Text>
                    </td>
                    <td>
                      <Text
                        weight={700}
                        sx={{ color: toneColor(s.show.convPctTone) }}
                      >
                        {s.show.convPct}
                      </Text>
                    </td>
                    <td>
                      <UnstyledButton
                        sx={(t) => ({
                          border: `1px solid ${
                            t.colorScheme === 'dark' ? t.colors.dark[3] : t.colors.gray[4]
                          }`,
                          borderRadius: 4,
                          padding: '2px 8px',
                          fontSize: 12,
                        })}
                      >
                        {s.show.cost}
                      </UnstyledButton>
                    </td>
                    <td>
                      <Text>{s.show.avgDays}</Text>
                    </td>
                    <td style={goalDividerStyle}>
                      <Text color="blue" weight={700}>
                        {s.purchase.conv}
                      </Text>
                    </td>
                    <td>
                      <Text
                        weight={700}
                        sx={{ color: toneColor(s.purchase.convPctTone) }}
                      >
                        {s.purchase.convPct}
                      </Text>
                    </td>
                    <td>
                      <UnstyledButton
                        sx={(t) => ({
                          border: `1px solid ${
                            t.colorScheme === 'dark' ? t.colors.dark[3] : t.colors.gray[4]
                          }`,
                          borderRadius: 4,
                          padding: '2px 8px',
                          fontSize: 12,
                        })}
                      >
                        {s.purchase.cost}
                      </UnstyledButton>
                    </td>
                    <td>
                      <Text>{s.purchase.avgDays}</Text>
                    </td>
                  </tr>
                ))}
              </>
            )}
          </tbody>
        </Table>
      </ScrollArea>

      <Flex gap="lg" mt="md" align="center" wrap="wrap">
        <Text size="xs" color="teal" weight={600}>
          Book Call
        </Text>
        <Text size="xs" sx={{ color: '#f59e0b' }} weight={600}>
          Show
        </Text>
        <Text size="xs" color="blue" weight={600}>
          Purchase
        </Text>
        <Text size="xs" color="dimmed">
          Cost/Goal = spend attributed to conversions at that goal
        </Text>
      </Flex>
    </Paper>
  );
};
